/** La bandeja: lista filtrable y el formulario para enviar una solicitud nueva. */
import { useCallback, useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

import { ErrorDeApi, api } from '../api'
import {
  Cargando,
  Dialogo,
  Error,
  EtiquetaDeEstado,
  EtiquetaDePrioridad,
  Vacio,
} from '../componentes/piezas'
import {
  BotonDeAdjuntar, PendientesDeSubir, manejadoresDeArchivos, useSubidas,
} from '../componentes/adjuntos'
import { usePortal } from '../contexto'
import { ESTADO_SOLICITUD, PRIORIDAD, hace } from '../textos'
import type { Bandeja, Prioridad, Solicitud } from '../tipos'

const POR_PAGINA = 50

export default function Solicitudes() {
  const { ruta, servidor, departamentos, puedo } = usePortal()
  const [parametros, setParametros] = useSearchParams()
  const [bandeja, setBandeja] = useState<Bandeja | null>(null)
  const [error, setError] = useState('')
  const [desde, setDesde] = useState(0)

  const estado = parametros.get('estado') ?? ''
  const departamento = parametros.get('departamento') ?? ''
  // Por defecto sólo las abiertas. «todas» es un valor propio: si fuera la ausencia del
  // parámetro, elegirla borraría el parámetro y volvería a aplicarse el de por defecto.
  const abiertas = parametros.get('abiertas') ?? 'si'
  const abierto = parametros.get('nueva') === '1'

  const cargar = useCallback(async () => {
    const consulta = new URLSearchParams()
    if (estado) consulta.set('estado', estado)
    if (departamento) consulta.set('departamento_id', departamento)
    if (abiertas === 'si') consulta.set('abiertas', 'true')
    if (abiertas === 'no') consulta.set('abiertas', 'false')
    consulta.set('limite', String(POR_PAGINA))
    consulta.set('desde', String(desde))
    try {
      setBandeja(await api.get<Bandeja>(`${ruta}/solicitudes?${consulta}`))
      setError('')
    } catch (e) {
      setError((e as ErrorDeApi).message)
    }
  }, [ruta, estado, departamento, abiertas, desde])

  useEffect(() => {
    setBandeja(null)
    void cargar()
  }, [cargar])

  function filtrar(clave: string, valor: string) {
    const siguientes = new URLSearchParams(parametros)
    if (valor) siguientes.set(clave, valor)
    else siguientes.delete(clave)
    // Si se pide un estado concreto (p. ej. «Completada»), seguir en «sólo abiertas» dejaría la
    // lista vacía: lo más específico manda.
    if (clave === 'estado' && valor) siguientes.set('abiertas', 'todas')
    siguientes.delete('nueva')
    setDesde(0)
    setParametros(siguientes)
  }

  const base = `/${servidor.id}`
  const nombreDeDepartamento = (id: number | null) =>
    departamentos.find((d) => d.id === id)?.nombre
  const colorDeDepartamento = (id: number | null) =>
    departamentos.find((d) => d.id === id)?.color || 'var(--texto-tenue)'

  return (
    <>
      <header className="cabecera">
        <h1>Solicitudes</h1>
        <div className="cabecera__acciones">
          {puedo('crear_solicitud') && (
            <button
              className="boton boton--principal"
              onClick={() => filtrarNueva(parametros, setParametros)}
            >
              Nueva solicitud
            </button>
          )}
        </div>
      </header>

      <div className="contenido">
        <div className="filtros">
          <select
            aria-label="Filtrar por estado"
            value={estado}
            onChange={(e) => filtrar('estado', e.target.value)}
          >
            <option value="">Todos los estados</option>
            {Object.entries(ESTADO_SOLICITUD).map(([clave, texto]) => (
              <option key={clave} value={clave}>{texto}</option>
            ))}
          </select>

          {departamentos.length > 0 && (
            <select
              aria-label="Filtrar por departamento"
              value={departamento}
              onChange={(e) => filtrar('departamento', e.target.value)}
            >
              <option value="">Todos los departamentos</option>
              {departamentos.map((d) => (
                <option key={d.id} value={d.id}>{d.nombre}</option>
              ))}
            </select>
          )}

          <select
            aria-label="Abiertas o cerradas"
            value={abiertas}
            onChange={(e) => filtrar('abiertas', e.target.value)}
          >
            <option value="si">Sólo abiertas</option>
            <option value="no">Sólo cerradas</option>
            <option value="todas">Todas</option>
          </select>

          {bandeja && (
            <span style={{ color: 'var(--texto-tenue)', fontSize: '0.85rem' }}>
              {bandeja.total} {bandeja.total === 1 ? 'solicitud' : 'solicitudes'}
            </span>
          )}
        </div>

        {error && <Error>{error}</Error>}
        {!bandeja && !error && <Cargando filas={5} />}

        {bandeja && bandeja.solicitudes.length === 0 && (
          <div className="tarjeta">
            <Vacio titulo="Aquí no hay nada">
              <p>
                {estado || departamento
                  ? 'Ninguna solicitud cumple los filtros. Prueba a quitarlos.'
                  : abiertas === 'si'
                    ? 'No hay solicitudes abiertas. Elige «Todas» para ver también las cerradas.'
                    : abiertas === 'no'
                      ? 'No hay solicitudes cerradas todavía.'
                      : 'Todavía no se ha enviado ninguna solicitud.'}
              </p>
            </Vacio>
          </div>
        )}

        {bandeja && bandeja.solicitudes.length > 0 && (
          <ul className="lista tarjeta">
            {bandeja.solicitudes.map((s) => (
              <li key={s.numero}>
                <Link className="fila" to={`${base}/solicitudes/${s.numero}`}>
                  <EtiquetaDePrioridad valor={s.prioridad ?? s.prioridad_pedida} />
                  <div className="fila__principal">
                    <div className="fila__titulo">{s.titulo}</div>
                    <div className="fila__meta">
                      <span className="codigo">{s.codigo}</span>
                      {nombreDeDepartamento(s.departamento_id) && (
                        <span className="depto">
                          <i style={{ background: colorDeDepartamento(s.departamento_id) }} />
                          {nombreDeDepartamento(s.departamento_id)}
                        </span>
                      )}
                      <span>{s.solicitante?.nombre ?? 'alguien'}</span>
                      <span>{hace(s.creada)}</span>
                      {s.responsable && <span>· lleva {s.responsable.nombre}</span>}
                    </div>
                  </div>
                  <EtiquetaDeEstado texto={s.estado_texto} />
                </Link>
              </li>
            ))}
          </ul>
        )}

        {bandeja && bandeja.total > POR_PAGINA && (
          <div className="fila-botones" style={{ marginTop: 14, justifyContent: 'center' }}>
            <button className="boton" disabled={desde === 0}
                    onClick={() => setDesde(Math.max(0, desde - POR_PAGINA))}>
              ← Anteriores
            </button>
            <span style={{ alignSelf: 'center', fontSize: '0.85rem', color: 'var(--texto-suave)' }}>
              {desde + 1}–{Math.min(desde + POR_PAGINA, bandeja.total)} de {bandeja.total}
            </span>
            <button className="boton" disabled={desde + POR_PAGINA >= bandeja.total}
                    onClick={() => setDesde(desde + POR_PAGINA)}>
              Siguientes →
            </button>
          </div>
        )}
      </div>

      {abierto && (
        <FormularioNueva
          alCerrar={() => filtrar('nueva', '')}
          alCrear={() => {
            filtrar('nueva', '')
            void cargar()
          }}
        />
      )}
    </>
  )
}

function filtrarNueva(
  parametros: URLSearchParams,
  setParametros: (p: URLSearchParams) => void,
) {
  const siguientes = new URLSearchParams(parametros)
  siguientes.set('nueva', '1')
  setParametros(siguientes)
}

function FormularioNueva({
  alCerrar,
  alCrear,
}: {
  alCerrar: () => void
  alCrear: () => void
}) {
  const { ruta, departamentos, avisar, yo } = usePortal()
  const [titulo, setTitulo] = useState('')
  const [descripcion, setDescripcion] = useState('')
  const [justificacion, setJustificacion] = useState('')
  const [impacto, setImpacto] = useState('')
  const [prioridad, setPrioridad] = useState<Prioridad>('medium')
  const mios = departamentos.filter((d) => yo.departamentos.includes(d.id))
  const elegibles = mios.length > 0 ? mios : departamentos
  const [departamento, setDepartamento] = useState('')

  // Los departamentos llegan por su cuenta, así que puede que en el primer dibujo aún no
  // estuvieran. Sin esto, el desplegable se quedaba en «Sin departamento» mientras el
  // servidor sí la archivaba en el único que hay: lo que se veía no era lo que pasaba.
  useEffect(() => {
    if (!departamento && elegibles.length === 1) setDepartamento(String(elegibles[0].id))
  }, [departamento, elegibles])
  const [error, setError] = useState('')
  const [enviando, setEnviando] = useState(false)
  // Las fotos se suben al elegirlas y se adjuntan al enviar la solicitud.
  const subidas = useSubidas(`${ruta}/archivos`)
  const arrastre = manejadoresDeArchivos(subidas)

  async function enviar() {
    if (subidas.subiendo) {
      setError('Espera a que terminen de subirse las fotos.')
      return
    }
    if (!titulo.trim()) {
      setError('Ponle un título: es lo que se ve en la bandeja.')
      return
    }
    setEnviando(true)
    setError('')
    try {
      const creada = await api.post<Solicitud>(`${ruta}/solicitudes`, {
        titulo,
        descripcion,
        justificacion,
        impacto,
        prioridad_pedida: prioridad,
        departamento_id: departamento ? Number(departamento) : null,
        adjuntos: subidas.tokens,
      })
      avisar(`Enviada como ${creada.codigo}.`, 'bien')
      alCrear()
    } catch (e) {
      setError((e as ErrorDeApi).message)
      setEnviando(false)
    }
  }

  return (
    <Dialogo
      titulo="Nueva solicitud de desarrollo"
      alCerrar={alCerrar}
      pie={
        <>
          <button className="boton" onClick={alCerrar} disabled={enviando}>Cancelar</button>
          <button className="boton boton--principal" onClick={enviar}
                  disabled={enviando || subidas.subiendo}>
            {enviando ? 'Enviando…' : subidas.subiendo ? 'Subiendo fotos…' : 'Enviar solicitud'}
          </button>
        </>
      }
    >
      {error && <Error>{error}</Error>}

      <div {...arrastre}>
      <div className="campo">
        <label htmlFor="titulo">Qué hace falta</label>
        <input
          id="titulo" value={titulo} autoFocus maxLength={200}
          onChange={(e) => setTitulo(e.target.value)}
          placeholder="Sistema de control de evidencias"
        />
      </div>

      <div className="campo">
        <label htmlFor="descripcion">Descripción</label>
        <textarea
          id="descripcion" value={descripcion}
          onChange={(e) => setDescripcion(e.target.value)}
          placeholder="Cuenta qué tendría que hacer y cómo os lo imagináis usando."
        />
      </div>

      <div className="campo">
        <label htmlFor="justificacion">Por qué hace falta</label>
        <textarea
          id="justificacion" value={justificacion}
          onChange={(e) => setJustificacion(e.target.value)}
          placeholder="Qué problema resuelve o qué se está haciendo a mano ahora."
        />
        <div className="campo__ayuda">
          Es lo que más mira administración para decidir. Cuanto más concreto, mejor.
        </div>
      </div>

      <div className="campo">
        <label htmlFor="impacto">A quién afecta</label>
        <textarea
          id="impacto" value={impacto} onChange={(e) => setImpacto(e.target.value)}
          placeholder="Cuánta gente lo usaría y con qué frecuencia."
        />
      </div>

      <div className="campo">
        <label>Fotos y archivos</label>
        <PendientesDeSubir subidas={subidas} />
        <BotonDeAdjuntar subidas={subidas} texto="Adjuntar fotos o archivos" />
        <div className="campo__ayuda">
          Capturas, vídeos cortos, PDF, documentos o logs. Puedes arrastrarlos aquí o pegar una
          captura con Ctrl+V.
        </div>
      </div>

      {elegibles.length > 0 && (
        <div className="campo">
          <label htmlFor="departamento">Departamento</label>
          <select id="departamento" value={departamento}
                  onChange={(e) => setDepartamento(e.target.value)}>
            <option value="">Sin departamento</option>
            {elegibles.map((d) => (
              <option key={d.id} value={d.id}>{d.nombre}</option>
            ))}
          </select>
        </div>
      )}

      <div className="campo">
        <label htmlFor="prioridad">Prioridad que pedís</label>
        <select id="prioridad" value={prioridad}
                onChange={(e) => setPrioridad(e.target.value as Prioridad)}>
          {Object.entries(PRIORIDAD).map(([clave, texto]) => (
            <option key={clave} value={clave}>{texto}</option>
          ))}
        </select>
        <div className="campo__ayuda">
          Administración pondrá la suya al revisarla; la vuestra se guarda igual.
        </div>
      </div>
      </div>
    </Dialogo>
  )
}
